<template>
  <v-container fluid class="engine-changelog">
    <div class="engine-changelog__header">
      <div class="engine-changelog__title">
        <v-icon left color="primary">mdi-history</v-icon>
        <span class="headline">Engine changelog</span>
        <v-spacer />
        <v-btn small outlined :loading="loading" @click="load">
          <v-icon small left>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </div>
      <div class="caption grey--text text--darken-1 engine-changelog__subtitle">
        What changed in each analysis engine, newest version first. A run measured with an older
        version is marked <strong>Outdated</strong
        ><span v-if="folderRunsEnabled"> in <router-link to="/runs">Runs</router-link></span
        >; this page says what that change was.
      </div>
    </div>

    <v-alert v-if="error" type="error" outlined class="engine-changelog__error">
      <div class="d-flex align-center">
        <span>{{ error }}</span>
        <v-spacer />
        <v-btn small outlined color="error" @click="load">
          <v-icon small left>mdi-refresh</v-icon>
          Retry
        </v-btn>
      </div>
    </v-alert>

    <v-skeleton-loader
      v-else-if="loading && !engines.length"
      type="heading, list-item-three-line@3"
      class="mt-4"
    />

    <v-card v-else-if="!engines.length" outlined class="engine-changelog__empty">
      <v-icon size="48" color="grey lighten-1">mdi-file-document-outline</v-icon>
      <div class="subtitle-1 grey--text text--darken-1 mt-2">No changelog available</div>
      <div class="caption grey--text">
        The analysis service did not return an engine changelog. Engine versions are still recorded
        on every run.
      </div>
    </v-card>

    <template v-else>
      <v-chip-group
        v-model="selectedEngine"
        column
        class="engine-changelog__filter"
        active-class="primary--text"
      >
        <v-chip v-for="engine in engines" :key="`chip-${engine.name}`" :value="engine.name" small>
          {{ engine.name }}
          <span class="caption grey--text ml-1">({{ engine.entries.length }})</span>
        </v-chip>
      </v-chip-group>

      <v-card
        v-for="engine in visibleEngines"
        :key="engine.name"
        outlined
        class="engine-changelog__engine"
      >
        <div class="engine-changelog__engine-head">
          <span class="subtitle-1 font-weight-bold">{{ engine.name }}</span>
          <v-chip
            v-if="engine.deployed"
            x-small
            label
            color="success"
            dark
            class="ml-2"
            title="Version deployed right now"
          >
            deployed {{ engine.deployed }}
          </v-chip>
        </div>

        <div
          v-for="entry in engine.entries"
          :key="`${engine.name}-${entry.version}`"
          class="engine-changelog__entry"
          :class="{ 'engine-changelog__entry--deployed': entry.isDeployed }"
        >
          <div class="engine-changelog__entry-head">
            <v-chip x-small label outlined class="engine-changelog__version">
              {{ entry.version }}
            </v-chip>
            <span class="caption grey--text ml-2">{{ entry.date }}</span>
            <v-chip v-if="entry.isDeployed" x-small label color="success" dark class="ml-2">
              current
            </v-chip>
          </div>
          <ul v-if="entry.changes.length" class="engine-changelog__changes body-2">
            <li v-for="(change, index) in entry.changes" :key="`${entry.version}-${index}`">
              {{ change }}
            </li>
          </ul>
          <div v-else class="caption grey--text">No details recorded for this version.</div>
        </div>
      </v-card>
    </template>
  </v-container>
</template>

<script>
import featureFlags from '@/config/featureFlags';
import VersionService from '@/services/VersionService';
import { EMPTY, compareSemverDesc, formatDateTime } from '@/utils/folderRuns';

/**
 * /engine-changelog — the static changelog served by `GET utils/engine-changelog`
 * (`{ "<engine>": [{ version, date, changes: string[] }] }`).
 *
 * Deliberately NOT behind the `folderRuns` flag: it is read-only reference material and it is
 * what makes an "Outdated" chip on /runs actionable. The deployed version is read from the
 * existing `utils/version` endpoint and, when available, marks the matching entry as current.
 */
export default {
  name: 'EngineChangelog',

  data() {
    return {
      changelog: {},
      deployedVersions: {},
      loading: false,
      error: '',
      selectedEngine: null,
    };
  },

  computed: {
    /** /runs is behind a flag, so the link to it must not turn into a dead end. */
    folderRunsEnabled() {
      return Boolean(featureFlags.folderRuns);
    },
    engines() {
      const raw = this.changelog && typeof this.changelog === 'object' ? this.changelog : {};
      return Object.keys(raw)
        .filter((name) => Array.isArray(raw[name]))
        .sort()
        .map((name) => {
          const deployed = this.deployedVersions[name] || null;
          const entries = raw[name]
            .filter((entry) => entry && typeof entry === 'object')
            .map((entry) => ({
              version: entry.version ? String(entry.version) : EMPTY,
              date: entry.date ? this.formatDay(entry.date) : EMPTY,
              changes: Array.isArray(entry.changes) ? entry.changes.map(String) : [],
              isDeployed: Boolean(deployed) && String(entry.version) === String(deployed),
            }))
            .sort((a, b) => compareSemverDesc(a.version, b.version));
          return { name, entries, deployed };
        })
        .filter((engine) => engine.entries.length);
    },
    visibleEngines() {
      if (!this.selectedEngine) return this.engines;
      return this.engines.filter((engine) => engine.name === this.selectedEngine);
    },
  },

  created() {
    this.load();
  },

  methods: {
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const response = await VersionService.engineChangelog();
        this.changelog = this.unwrap(response?.data);
      } catch (error) {
        this.changelog = {};
        this.error = 'Failed to load the engine changelog.';
      } finally {
        this.loading = false;
      }
      this.loadDeployedVersions();
    },

    /** Best effort: the page is useful without it, so a failure is silent. */
    async loadDeployedVersions() {
      try {
        const response = await VersionService.get();
        const body = response?.data?.data ?? response?.data ?? {};
        const engines = body?.ai_service?.engines;
        this.deployedVersions = engines && typeof engines === 'object' ? engines : {};
      } catch (error) {
        this.deployedVersions = {};
      }
    },

    /**
     * Node answers with the bare `{ "<engine>": [...] }` body. The envelope is only peeled when
     * the payload is literally `{ data: {...} }`, so an engine that happened to be called "data"
     * would still survive.
     */
    unwrap(payload) {
      if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return {};
      const keys = Object.keys(payload);
      if (keys.length === 1 && keys[0] === 'data' && !Array.isArray(payload.data)) {
        return payload.data && typeof payload.data === 'object' ? payload.data : {};
      }
      return payload;
    },

    /** Changelog dates are days ('2026-09-09'); never print a spurious 00:00. */
    formatDay(value) {
      const text = String(value);
      if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
      return formatDateTime(value);
    },
  },
};
</script>

<style scoped>
.engine-changelog {
  padding-top: 72px;
  max-width: 1100px;
}
.engine-changelog__header {
  margin-bottom: 12px;
}
.engine-changelog__title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.engine-changelog__subtitle {
  margin-top: 4px;
  max-width: 760px;
}
.engine-changelog__filter {
  margin-bottom: 8px;
}
.engine-changelog__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 16px;
  text-align: center;
  border-radius: 10px !important;
}
.engine-changelog__engine {
  border-radius: 10px !important;
  padding: 14px 16px;
  margin-bottom: 12px;
}
.engine-changelog__engine-head {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}
.engine-changelog__entry {
  border-left: 2px solid #e0e0e0;
  padding: 6px 0 6px 12px;
  margin-left: 4px;
}
.engine-changelog__entry--deployed {
  border-left-color: #43a047;
}
.engine-changelog__entry-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.engine-changelog__version {
  font-weight: 700;
}
.engine-changelog__changes {
  margin: 4px 0 0;
  padding-left: 18px;
}
.engine-changelog__changes li {
  margin-bottom: 2px;
}
</style>
